import { useMemo } from "react";
import { getAttendanceStatus, getBudgetStatus } from "./dashboardDerivedUtils";

export default function useDashboardDerivedData({
  students,
  teachers,
  schedules,
  finances,
  attendanceLogs,
  visitLogs,
  selectedDepartment,
  annualBudget,
  dismissedRadarIds,
  icons,
}) {
  const filteredStudents = useMemo(() => {
    if (!selectedDepartment || selectedDepartment === "All") return students;
    return students.filter((student) => (student.department || student.grade) === selectedDepartment);
  }, [students, selectedDepartment]);

  const filteredSchedules = useMemo(() => {
    if (!selectedDepartment || selectedDepartment === "All") return schedules;
    return schedules.filter((schedule) => !schedule.department || schedule.department === "All" || schedule.department === selectedDepartment);
  }, [schedules, selectedDepartment]);

  const filteredFinances = useMemo(() => {
    if (!selectedDepartment || selectedDepartment === "All") return finances;
    return finances.filter((finance) => !finance.department || finance.department === "All" || finance.department === selectedDepartment);
  }, [finances, selectedDepartment]);

  const reportStats = useMemo(() => {
    const studentIds = new Set(filteredStudents.map((student) => student.id));

    const recentLogs = [...attendanceLogs]
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(-4);

    let attendanceRate = 0;
    let attendanceDelta = 0;

    if (recentLogs.length > 0) {
      const getRate = (log) => {
        const count = Object.entries(log).filter(([id, status]) => studentIds.has(id) && (status === "present" || status === "late")).length;
        return studentIds.size > 0 ? Math.round((count / studentIds.size) * 100) : 0;
      };
      attendanceRate = getRate(recentLogs[recentLogs.length - 1]);
      const prevRate = recentLogs.length > 1 ? getRate(recentLogs[recentLogs.length - 2]) : Math.max(0, attendanceRate - 5);
      attendanceDelta = attendanceRate - prevRate;
    }

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const visitedIds = new Set(visitLogs.filter((log) => studentIds.has(log.studentId) && new Date(log.date) >= thirtyDaysAgo).map((log) => log.studentId));
    const visitationCount = visitedIds.size;
    const visitationRate = studentIds.size > 0 ? Math.round((visitationCount / studentIds.size) * 100) : 0;

    const balance = filteredFinances.reduce((acc, finance) => acc + (finance.type === "income" ? finance.amount : -finance.amount), 0);

    const longTermAbsentees = filteredStudents.filter((student) => {
      if (recentLogs.length < 4) return false;
      return recentLogs.slice(-4).every((log) => !log[student.id] || log[student.id] === "absent");
    });

    return {
      studentCount: filteredStudents.length,
      studentDelta: 0,
      attendanceRate,
      attendanceDelta,
      visitationCount,
      visitationRate,
      financeBalance: balance,
      financeDelta: 0,
      absentees: longTermAbsentees,
    };
  }, [filteredStudents, attendanceLogs, visitLogs, filteredFinances]);

  const filteredTeachersForBirthday = useMemo(() => {
    if (!selectedDepartment || selectedDepartment === "All") return teachers;
    return teachers.filter((teacher) => teacher.charge && teacher.charge.includes(selectedDepartment));
  }, [teachers, selectedDepartment]);

  const birthdayStudents = useMemo(() => {
    const currentMonthStr = String(new Date().getMonth() + 1).padStart(2, "0");

    const allPeople = [
      ...filteredStudents.map((student) => ({ ...student, type: "student" })),
      ...filteredTeachersForBirthday.map((teacher) => ({ ...teacher, type: "teacher" })),
    ];

    return allPeople.filter((person) => {
      if (!person.birth) return false;
      const birthStr = typeof person.birth === "string" ? person.birth : person.birth?.toDate ? person.birth.toDate().toISOString().split("T")[0] : String(person.birth);
      const parts = birthStr.split(/[.\-\/]/);
      if (parts.length >= 2) {
        const monthPart = parts.length >= 3 ? parts[1] : parts[0];
        return monthPart?.trim()?.padStart(2, "0") === currentMonthStr;
      }
      return false;
    });
  }, [filteredStudents, filteredTeachersForBirthday]);

  const currentBudget = useMemo(() => {
    if (typeof annualBudget === "object" && annualBudget) {
      return annualBudget[selectedDepartment] || 0;
    }
    return Number(annualBudget) || 0;
  }, [annualBudget, selectedDepartment]);

  const currentYear = new Date().getFullYear();
  const yearlyExpense = filteredFinances
    .filter((finance) => finance.type === "지출" && finance.date.startsWith(String(currentYear)))
    .reduce((acc, cur) => acc + parseInt(cur.amount || 0, 10), 0);

  const yearlyIncome = filteredFinances
    .filter((finance) => finance.type === "수입" && finance.date.startsWith(String(currentYear)))
    .reduce((acc, cur) => acc + parseInt(cur.amount || 0, 10), 0);

  const remainingBudget = currentBudget + yearlyIncome - yearlyExpense;
  const avgAttendance = reportStats?.attendanceRate || 0;

  const nextBirthdayStudent = useMemo(() => {
    const today = new Date();
    const sorted = [...filteredStudents]
      .map((student) => {
        if (!student.birth) return null;

        const birthStr = typeof student.birth === "string" ? student.birth : student.birth?.toDate ? student.birth.toDate().toISOString().split("T")[0] : String(student.birth);
        const parts = birthStr.split(/[.\-\/]/);
        if (parts.length < 2) return null;

        const m = parts.length >= 3 ? Number(parts[1]) : Number(parts[0]);
        const d = parts.length >= 3 ? Number(parts[2]) : Number(parts[1]);
        if (isNaN(m) || isNaN(d)) return null;

        const nextBirth = new Date(today.getFullYear(), m - 1, d);
        if (nextBirth < today) nextBirth.setFullYear(today.getFullYear() + 1);

        return { ...student, nextBirth };
      })
      .filter(Boolean)
      .sort((a, b) => a.nextBirth - b.nextBirth);

    return sorted[0];
  }, [filteredStudents]);

  const getBirthdayStatus = (count) => {
    if (count > 0) return { icon: icons.party, text: `${count}명 축하해요!` };

    if (nextBirthdayStudent) {
      const hasValidPhoto = nextBirthdayStudent.photoUrl && nextBirthdayStudent.photoUrl.trim() !== "";
      return {
        icon: hasValidPhoto ? nextBirthdayStudent.photoUrl : icons.birthday,
        text: `다음: ${nextBirthdayStudent.name}`,
        isPhoto: hasValidPhoto,
      };
    }

    return { icon: icons.check, text: "생일자 없음" };
  };

  const radarRecommendations = useMemo(() => {
    const recs = [];
    const today = new Date();
    const oneWeekLater = new Date(today);
    oneWeekLater.setDate(today.getDate() + 7);

    filteredStudents.forEach((student) => {
      if (student.birth) {
        const birthStr = typeof student.birth === "string" ? student.birth : student.birth?.toDate ? student.birth.toDate().toISOString().split("T")[0] : String(student.birth);
        const cleanBirth = birthStr.replace(/\./g, "-");
        const birthDate = new Date(cleanBirth);

        if (!isNaN(birthDate)) {
          const thisYearBirth = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate());
          const isToday = thisYearBirth.toDateString() === today.toDateString();

          if (thisYearBirth >= today && thisYearBirth <= oneWeekLater) {
            const id = `birth-${student.id}`;
            if (!dismissedRadarIds.includes(id)) {
              recs.push({
                id,
                studentId: student.id,
                name: student.name,
                reason: isToday ? "오늘 생일이에요! ✨" : "이번 주 생일 🎂",
                action: "축복의 메시지를 보내주세요",
                color: isToday ? "bg-pink-100 text-pink-600 animate-pulse-subtle" : "bg-yellow-100 text-yellow-600",
                btnColor: isToday ? "bg-pink-500" : "bg-yellow-500",
                btnText: "축하",
                priority: isToday ? 100 : 80,
              });
            }
          }
        }
      }

      let lastContactDate = student.createdAt?.toDate ? student.createdAt.toDate() : new Date(student.createdAt || 0);

      if (student.lastVisit && student.lastVisit !== "-") {
        const lv = new Date(student.lastVisit);
        if (!isNaN(lv) && lv > lastContactDate) lastContactDate = lv;
      }

      if (visitLogs && visitLogs.length > 0) {
        const studentLogs = visitLogs.filter((log) => log.studentId === student.id);
        if (studentLogs.length > 0) {
          studentLogs.sort((a, b) => new Date(b.date) - new Date(a.date));
          const lastLogDate = new Date(studentLogs[0].date);
          if (lastLogDate > lastContactDate) lastContactDate = lastLogDate;
        }
      }

      const daysSinceContact = Math.floor((today - lastContactDate) / (1000 * 60 * 60 * 24));
      if (daysSinceContact >= 21) {
        const id = `absent-${student.id}`;
        if (!dismissedRadarIds.includes(id)) {
          recs.push({
            id,
            studentId: student.id,
            name: student.name,
            reason: "3주 이상 미접촉 🚨",
            action: "따뜻한 안부 전화 어떠신가요?",
            color: "bg-red-100 text-red-600",
            btnColor: "bg-red-500",
            btnText: "전화",
            priority: 90,
          });
        }
      }

      const recentCounseling = visitLogs?.find(
        (log) =>
          log.studentId === student.id &&
          (log.type === "상담" || log.type === "기도") &&
          Math.floor((today - new Date(log.date)) / (1000 * 60 * 60 * 24)) <= 7
      );

      if (recentCounseling) {
        const id = `followup-${student.id}`;
        if (!dismissedRadarIds.includes(id)) {
          recs.push({
            id,
            studentId: student.id,
            name: student.name,
            reason: "상담 리마인드 💭",
            action: "지난 상담 후 변화를 체크해보세요",
            color: "bg-indigo-100 text-indigo-600",
            btnColor: "bg-indigo-500",
            btnText: "팔로우업",
            priority: 70,
          });
        }
      }

      if (student.createdAt) {
        const regDate = student.createdAt.toDate ? student.createdAt.toDate() : new Date(student.createdAt);
        if (!isNaN(regDate)) {
          const daysSinceReg = Math.floor((today - regDate) / (1000 * 60 * 60 * 24));
          if (daysSinceReg <= 14 && daysSinceReg >= 0) {
            const id = `new-${student.id}`;
            if (!dismissedRadarIds.includes(id)) {
              recs.push({
                id,
                studentId: student.id,
                name: student.name,
                reason: "우리 부서 새싹 🌱",
                action: "공동체에 잘 적응하고 있나요?",
                color: "bg-emerald-100 text-emerald-600",
                btnColor: "bg-emerald-500",
                btnText: "확인",
                priority: 60,
              });
            }
          }
        }
      }
    });

    if (recs.length === 0 && filteredStudents.length > 0) {
      const dayIndex = today.getDate();
      const randomStudent = filteredStudents[dayIndex % filteredStudents.length];
      const id = `pray-${randomStudent.id}`;

      if (!dismissedRadarIds.includes(id)) {
        recs.push({
          id,
          studentId: randomStudent.id,
          name: randomStudent.name,
          reason: "오늘의 중보기도 🙏",
          action: "기도하기",
          color: "bg-blue-100 text-blue-600",
          btnColor: "bg-blue-500",
          btnText: "기도",
        });
      }
    }

    return recs
      .sort((a, b) => (b.priority || 0) - (a.priority || 0))
      .slice(0, 5)
      .map((recommendation) => ({
        ...recommendation,
        icon: recommendation.id.startsWith("birth")
          ? icons.birthday
          : recommendation.id.startsWith("absent")
            ? icons.siren
            : recommendation.id.startsWith("new")
              ? icons.glowingstar
              : recommendation.id.startsWith("followup")
                ? icons.lightbulb
                : icons.hearthands,
      }));
  }, [filteredStudents, visitLogs, dismissedRadarIds, icons]);

  const totalStudents = filteredStudents.length;
  const attStatus = getAttendanceStatus(avgAttendance, icons);
  const budgetStatus = getBudgetStatus(remainingBudget, annualBudget, icons);
  const birthStatus = getBirthdayStatus(birthdayStudents.length);

  return {
    filteredStudents,
    filteredSchedules,
    filteredFinances,
    totalStudents,
    birthdayStudents,
    remainingBudget,
    avgAttendance,
    attStatus,
    budgetStatus,
    birthStatus,
    radarRecommendations,
  };
}
